/**
 * Session-level type definitions.
 *
 * Mirrors the SessionConfig contract in /design/specs/4_architecture_c4_l3_components.md §2.2
 * (with some V1-deferred fields like flop/turn/river constraints omitted from
 * this first slice).
 */

export type PotType = 'SRP' | '3BP' | 'Any';
export type Position = 'IP' | 'OOP' | 'Any';
export type Archetype = 'Fish' | 'Whale' | 'TRec' | 'Any';
export type PreflopAnimation = 'Watch' | 'Skip';
export type DifficultyPreset = 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert' | 'Custom';

/**
 * Session config — the player's choices on screen 03. Persisted to localStorage
 * via PersistenceAdapter and consumed by SessionManager.startSession().
 */
export interface SessionConfig {
  potType: PotType;
  position: Position;
  archetype: Archetype;
  handsCount: number;
  timerSeconds: number | null;
  preflopAnimation: PreflopAnimation;
  sessionFocus: string | null;
}

/**
 * Difficulty preset table — per PRD V1 §7.1.
 *
 * Settings the preset controls: Timer, Preflop animation, Session length.
 * Other settings (Pot Type, Position, etc.) remain independently set by the user.
 */
export const DIFFICULTY_PRESETS: Record<
  Exclude<DifficultyPreset, 'Custom'>,
  Pick<SessionConfig, 'timerSeconds' | 'preflopAnimation' | 'handsCount'>
> = {
  Beginner:     { timerSeconds: 60, preflopAnimation: 'Watch', handsCount: 5  },
  Intermediate: { timerSeconds: 45, preflopAnimation: 'Skip',  handsCount: 8  },
  Advanced:     { timerSeconds: 30, preflopAnimation: 'Skip',  handsCount: 10 },
  Expert:       { timerSeconds: 15, preflopAnimation: 'Skip',  handsCount: 15 },
};

/** Default config when no prior session is stored. */
export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  potType: 'Any',
  position: 'Any',
  archetype: 'Fish',
  handsCount: 10,
  timerSeconds: 30,
  preflopAnimation: 'Skip',
  sessionFocus: null,
};

/**
 * Session — a started instance of a SessionConfig with metadata. Created at
 * Start Session time.
 */
export interface Session {
  id: string;
  config: SessionConfig;
  autoName: string;     // e.g. "May 27 7:41pm · SRP IP · Fish"
  startedAt: string;    // ISO timestamp
  /**
   * The currently active hand, if any. Set when the first hand is sampled at
   * Start Session time; replaced as hands progress. Null between hands and
   * before the first hand is sampled.
   */
  activeHand: import('./handState').HandState | null;
  /**
   * Resolved config snapshot for the active hand — captures what `Any` values
   * the engine picked when sampling this hand. Null when activeHand is null.
   */
  activeHandResolved: ResolvedHandConfig | null;
  /**
   * Grade for the current active hand, set by `sessionManager.attachGrade`
   * once GradeService returns a 'graded' outcome. Tagged with `handId` so
   * PostHandGrade can verify the persisted grade belongs to the current
   * activeHand (rather than a stale grade from a previous hand that the
   * Session object carried over). Null while a hand is still in progress
   * or before the grade pipeline has been run.
   */
  activeHandGrade?: {
    handId: string;
    grade: import('./grade').RubricGrade;
  } | null;
}

/**
 * Resolved configuration snapshot for a sampled hand. Mirrors the Any-stripped
 * fields from SessionConfig that the engine has committed to for this hand.
 */
export interface ResolvedHandConfig {
  potType: Exclude<PotType, 'Any'>;
  position: Exclude<Position, 'Any'>;
  archetype: Exclude<Archetype, 'Any'>;
}

/**
 * Generate the auto-name per PRD V1 §7.3:
 *   [Date] [Time] · [Pot Type] [Position] · [Archetype]
 *   e.g. "May 27 7:41pm · SRP IP · Fish"
 */
export function generateAutoName(config: SessionConfig, when: Date = new Date()): string {
  const date = when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = when
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(' AM', 'am')
    .replace(' PM', 'pm');
  return `${date} ${time} · ${config.potType} ${config.position} · ${config.archetype}`;
}
